import { memo, useCallback, useEffect, useState } from "react";
import { CartContentHeader } from "./cartContentHeader";
import { CartItem } from "./cartItem";
import styles from "./style.module.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { removeItem } from "../../redux/slices/artItemSlice";

interface ICartContentProps {
  onCartUpdate: (cartData: { total: number; itemCount: number }) => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  companyName: string;
  payerEmail: string;
  phone: string;
  country: string;
  streetAddress: string;
  apartmentSuite: string;
  state: string;
  city: string;
}

const CartContentComponent = ({ onCartUpdate }: ICartContentProps) => {
   const dispatch = useDispatch();
  const cartItems = useSelector((state: RootState) => state.ArtItem.items);
  const isCheckoutOpen = useSelector((state: RootState) => state.CheckoutState.isCheckoutOpen)

  const calculateTotalPrice = useCallback(() => {
    return cartItems.reduce((total, item) => {
      const numericPrice = parseFloat(item.itemPrice.replace(/[$,]/g, ""));
      return total + numericPrice;
    }, 0);
  }, [cartItems]);

   // === ADD THIS CODE BLOCK ===
  useEffect(() => {
    // This runs ONLY when the component is first mounted
    console.log('%c CartContent ==> MOUNTED', 'background: #28a745; color: #fff; padding: 2px;');

    return () => {
      // This runs ONLY when the component is about to be unmounted
      console.log('%c CartContent ==> UNMOUNTING', 'background: #dc3545; color: #fff; padding: 2px;');
    };
  }, []); // The empty array [] is crucial. It means this effect runs only once in the component's lifecycle.
  // ============================

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    companyName: '',
    payerEmail: '',
    phone: '',
    country: '',
    streetAddress: '',
    apartmentSuite: '',
    state: '',
    city: ''
  });

  // Debug: Log form data changes
  console.log('Current formData:', formData);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    console.log('Input changed:', name, value); // Debug log
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

const total = calculateTotalPrice();
const itemCount = cartItems.length;

useEffect(() => {
  onCartUpdate({ total, itemCount });
}, [total, itemCount, onCartUpdate]);

  const handleRemoveItem = (index: number) => {
    dispatch(removeItem(index));
  };

  const itemText = cartItems.length === 1 ? "Item" : "Items";
  const headerTitle = isCheckoutOpen ? "Billing Details" : `Cart (${cartItems.length} ${itemText})`;

  useEffect(() => {
    if (isCheckoutOpen) {
      document.documentElement.style.setProperty("--dynamic-width", "38.47%");
    } else {
      document.documentElement.style.setProperty("--dynamic-width", "52.77%");
    }
  }, [isCheckoutOpen]);

  return (
    <div className={styles.container}>
      <CartContentHeader showBackButton={isCheckoutOpen} headerTitle={headerTitle} />

      {!isCheckoutOpen ? (
        // Cart Items
        cartItems.map((item, index) => (
          <CartItem
            key={index}
            itemImage={item.itemImage}
            itemQty={{ handleClick: () => handleRemoveItem(index), index }}
            itemDesc={{
              itemName: item.itemName,
              artistName: item.artistName,
              itemSize: item.itemSize,
              itemPrice: item.itemPrice,
            }}
          />
        ))
      ) : (
        // Billing Details
        <div className={styles.checkoutFormContainer}>
          <form className={styles.checkoutForm} onSubmit={(e) => e.preventDefault()}>
            <div className={styles.namesContainer}>
              <div className={styles.fName}>
                <label htmlFor="firstName">First Name</label>
                <input 
                  type="text" 
                  id="firstName" 
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                />
              </div>

              <div className={styles.lName}>
                <label htmlFor="lastName">Last Name</label>
                <input 
                  type="text" 
                  id="lastName" 
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className={styles.formEntry}>
              <label htmlFor="companyName">Company Name (optional)</label>
              <input 
                type="text" 
                id="companyName" 
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                autoComplete="organization"
              />
            </div>

            <div className={styles.formEntry}>
              <label htmlFor="payerEmail">Email</label>
              <input 
                type="email" 
                id="payerEmail" 
                name="payerEmail"
                value={formData.payerEmail}
                onChange={handleInputChange}
                autoComplete="email"
              />
            </div>

            <div className={styles.formEntry}>
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                pattern="^(\+?234|0)[789][01]\d{8}$"
                value={formData.phone}
                onChange={handleInputChange}
                autoComplete="tel"
              />
            </div>

            <div className={styles.formEntry}>
              <label htmlFor="country">Country/Region</label>
              <select 
                id="country" 
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                autoComplete="country"
              >
                <option value="">Select a country</option>
                <option value="NG">Nigeria</option>
              </select>
            </div>

            <div className={styles.formEntry}>
              <label htmlFor="streetAddress">Street Address</label>
              <input 
                type="text" 
                id="streetAddress" 
                name="streetAddress"
                placeholder="Street and House Number"
                value={formData.streetAddress}
                onChange={handleInputChange}
                autoComplete="street-address"
              />
              <input 
                type="text" 
                id="apartmentSuite" 
                name="apartmentSuite"
                placeholder="Apartment, Suite... (optional)"
                value={formData.apartmentSuite}
                onChange={handleInputChange}
                autoComplete="address-line2"
                style={{ marginTop: '8px' }}
              />
            </div>

            <div className={styles.formEntry}>
              <label htmlFor="state">State</label>
              <input 
                type="text" 
                id="state" 
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                autoComplete="address-level1"
              />
            </div>

            <div className={styles.formEntry}>
              <label htmlFor="city">Town/City</label>
              <input 
                type="text" 
                id="city" 
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                autoComplete="address-level2"
              />
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export const CartContent = memo(CartContentComponent);